import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

const EditModeContext = createContext(null);

const PANEL_STORAGE_KEY = 'psag-editor-panel-open';
const CONTROLS_STORAGE_KEY = 'psag-editor-controls-visible';
const BANNER_STORAGE_KEY = 'psag-editor-banner-open';

function readStoredBoolean(key, fallback) {
  try {
    const value = sessionStorage.getItem(key);
    if (value === null) return fallback;
    return value === 'true';
  } catch {
    return fallback;
  }
}

export function EditModeProvider({ children }) {
  const { isEditing } = useAuth();
  const [controlsVisible, setControlsVisible] = useState(() => readStoredBoolean(CONTROLS_STORAGE_KEY, true));
  const [panelOpen, setPanelOpen] = useState(() => readStoredBoolean(PANEL_STORAGE_KEY, false));
  const [bannerOpen, setBannerOpen] = useState(() => readStoredBoolean(BANNER_STORAGE_KEY, false));
  const [activeFieldKey, setActiveFieldKey] = useState(null);
  const [richTextElement, setRichTextElement] = useState(null);

  useEffect(() => {
    if (!isEditing) {
      setActiveFieldKey(null);
      setRichTextElement(null);
      return undefined;
    }

    document.body.classList.add('edit-mode-active');
    document.body.classList.toggle('edit-controls-hidden', !controlsVisible);
    document.body.classList.toggle('edit-panel-open', panelOpen);
    document.body.classList.toggle('edit-banner-open', bannerOpen);

    return () => {
      document.body.classList.remove('edit-mode-active', 'edit-controls-hidden', 'edit-panel-open', 'edit-banner-open');
    };
  }, [isEditing, controlsVisible, panelOpen, bannerOpen]);

  useEffect(() => {
    if (isEditing) return;
    setPanelOpen(false);
    setBannerOpen(false);
    setActiveFieldKey(null);
    setRichTextElement(null);
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    sessionStorage.setItem(BANNER_STORAGE_KEY, String(bannerOpen));
  }, [bannerOpen, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    sessionStorage.setItem(CONTROLS_STORAGE_KEY, String(controlsVisible));
  }, [controlsVisible, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    sessionStorage.setItem(PANEL_STORAGE_KEY, String(panelOpen));
  }, [panelOpen, isEditing]);

  const toggleControls = useCallback(() => {
    setControlsVisible((prev) => !prev);
    setActiveFieldKey(null);
  }, []);

  const togglePanel = useCallback(() => {
    setPanelOpen((prev) => !prev);
  }, []);

  const minimizeBanner = useCallback(() => {
    setBannerOpen(false);
  }, []);

  const expandBanner = useCallback(() => {
    setBannerOpen(true);
  }, []);

  const toggleBanner = useCallback(() => {
    setBannerOpen((prev) => !prev);
  }, []);

  const clearActiveField = useCallback(() => {
    setActiveFieldKey(null);
  }, []);

  const registerRichTextElement = useCallback((element) => {
    setRichTextElement(element);
  }, []);

  const unregisterRichTextElement = useCallback((element) => {
    setRichTextElement((current) => (current === element ? null : current));
  }, []);

  const focusField = useCallback((contentKey) => {
    setActiveFieldKey((prev) => {
      const nextKey = prev === contentKey ? null : contentKey;

      if (nextKey) {
        window.requestAnimationFrame(() => {
          const selector = `[data-content-key="${CSS.escape(contentKey)}"]`;
          const element = document.querySelector(selector);
          if (!element) return;

          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('editable-field--pulse');
          window.setTimeout(() => element.classList.remove('editable-field--pulse'), 1600);
        });
      }

      return nextKey;
    });
  }, []);

  const value = useMemo(
    () => ({
      controlsVisible,
      panelOpen,
      bannerOpen,
      activeFieldKey,
      richTextElement,
      toggleControls,
      togglePanel,
      minimizeBanner,
      expandBanner,
      toggleBanner,
      focusField,
      clearActiveField,
      registerRichTextElement,
      unregisterRichTextElement,
      setPanelOpen,
      setControlsVisible,
    }),
    [
      controlsVisible,
      panelOpen,
      bannerOpen,
      activeFieldKey,
      richTextElement,
      toggleControls,
      togglePanel,
      minimizeBanner,
      expandBanner,
      toggleBanner,
      focusField,
      clearActiveField,
      registerRichTextElement,
      unregisterRichTextElement,
    ]
  );

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}

export function useEditMode() {
  const context = useContext(EditModeContext);
  if (!context) {
    throw new Error('useEditMode must be used within EditModeProvider');
  }
  return context;
}
